import axios from "../../../../../api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for Suite Inspection Checklist operations
 */
export const ENDPOINTS = {
  BASE: "/v2/suite-inspection-checklists",
  CREATE: "/v2/suite-inspection-checklists",
  MASTER_SHEET_UNITS: "/v2/suite-inspection-checklists/master-sheet-units",
  GET_ALL: "/v2/suite-inspection-checklists",
  GET_BY_ID: "/v2/suite-inspection-checklists/:id",
  GET_HISTORY: "/v2/suite-inspection-checklists/:id/history",
  UPDATE: "/v2/suite-inspection-checklists/:id",
  DELETE: "/v2/suite-inspection-checklists/:id",
  SIGNED_URLS: "/v2/suite-inspection-checklists/signed-urls",
  UPLOAD_REQUEST: "/v2/suite-inspection-checklists/:id/upload-request",
  CONFIRM_UPLOAD: "/v2/suite-inspection-checklists/:id/confirm-upload",
  EXPORT_EXCEL: "/v2/suite-inspection-checklists/export-excel",
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Fallback message if no specific message is available
 */
const handleApiError = (error, defaultMessage) => {
  const message =
    error.response?.data?.message ||
    error.message ||
    defaultMessage ||
    "An unexpected error occurred";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

/**
 * Create a new Suite Inspection Checklist
 * @param {Object} payload - Inspection data including type, unit_number, cleanliness, property_details, etc.
 * @returns {Promise<Object>} The created inspection object (with enriched data)
 */
export const createSuiteInspection = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to create suite inspection checklist");
  }
};

/**
 * Fetch uninspected / available units from MasterSheet (for dropdowns / pending list)
 * @param {string} propertyId 
 * @param {string} buildingName 
 * @returns {Promise<Array>} List of master sheet units
 */
export const getMasterSheetUnits = async (propertyId, buildingName) => {
  try {
    const response = await axios.get(ENDPOINTS.MASTER_SHEET_UNITS, {
      params: { propertyId, buildingName },
    });
    return response.data.data || response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch units from master sheet");
  }
};

/**
 * Fetch all Suite Inspection Checklists with optional filters
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.propertyId] - Filter by property ID
 * @param {string} [params.unit_number] - Filter by unit number (partial match)
 * @param {string} [params.type] - Filter by type ("apartment" or "townhouse")
 * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
 * @param {string} [params.endDate] - End date (YYYY-MM-DD)
 * @param {string} [params.overall_status] - Filter by status ("passed" or "has issues")
 * @returns {Promise<{ success: boolean, count: number, data: Array, appliedFilters: Object }>}
 */
export const getSuiteInspectionList = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ALL, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch suite inspection checklists");
  }
};

/**
 * Fetch a single Suite Inspection Checklist by ID
 * @param {string} id - The inspection document _id
 * @returns {Promise<Object>} Detailed inspection object (enriched)
 */
export const getSuiteInspectionById = async (id) => {
  try {
    const url = ENDPOINTS.GET_BY_ID.replace(":id", id);
    const response = await axios.get(url);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch suite inspection checklist details");
  }
};

/**
 * Fetch change history / audit trail for a specific Suite Inspection
 * @param {string} id - The inspection document _id
 * @returns {Promise<{ unit_number: string, count: number, data: Array }>} History entries sorted newest → oldest
 */
export const getSuiteInspectionHistory = async (id) => {
  try {
    const url = ENDPOINTS.GET_HISTORY.replace(":id", id);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch suite inspection history");
  }
};

/**
 * Update an existing Suite Inspection Checklist
 * @param {string} id - Inspection ID
 * @param {Object} payload - Fields to update (e.g. cleanliness, comments, overall_status, attachments, etc.)
 * @returns {Promise<Object>} Updated inspection object
 */
export const updateSuiteInspection = async (id, payload) => {
  try {
    const url = ENDPOINTS.UPDATE.replace(":id", id);
    const response = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to update suite inspection checklist");
  }
};

/**
 * Delete a Suite Inspection Checklist
 * @param {string} id - Inspection ID
 * @returns {Promise<Object>} Deletion result (usually { success: true, message })
 */
export const deleteSuiteInspection = async (id) => {
  try {
    const url = ENDPOINTS.DELETE.replace(":id", id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete suite inspection checklist");
  }
};

/**
 * Request a presigned URL for direct file upload to S3
 * @param {string} inspectionId - Suite Inspection Checklist ID
 * @param {string} fileName - Original filename (used to generate extension)
 * @param {string} contentType - MIME type (e.g. "image/jpeg", "application/pdf")
 * @returns {Promise<{ presignedUrl: string, key: string, expectedContentType: string, expiresIn: number }>}
 */
export const requestSuiteInspectionUploadUrl = async (inspectionId, fileName, contentType) => {
  try {
    const url = ENDPOINTS.UPLOAD_REQUEST.replace(":id", inspectionId);
    const response = await axios.post(url, { fileName, contentType });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to obtain presigned upload URL for suite inspection");
  }
};

/**
 * Confirm that a file upload to S3 was successful and register it with the inspection
 * @param {string} inspectionId - Suite Inspection Checklist ID
 * @param {string} key - The S3 object key returned after successful upload
 * @returns {Promise<{ key: string, attachments: string[] }>}
 */
export const confirmSuiteInspectionUpload = async (inspectionId, key) => {
  try {
    const url = ENDPOINTS.CONFIRM_UPLOAD.replace(":id", inspectionId);
    const response = await axios.post(url, { key });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to confirm attachment upload for suite inspection");
  }
};

/**
 * Get temporary signed URLs for viewing/downloading attachments
 * @param {string[]} keys - Array of S3 object keys
 * @returns {Promise<Record<string, string | null>>} { [key]: signedUrl | null }
 */
export const getSuiteInspectionAttachmentSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) {
    return {};
  }

  try {
    const response = await axios.post(ENDPOINTS.SIGNED_URLS, { keys });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to generate signed URLs for suite inspection attachments");
    return {};
  }
};

/**
 * Export filtered Suite Inspection Checklists to Excel file and trigger download
 * @param {Object} params - Query parameters (same as getSuiteInspectionList)
 * @param {string} [params.propertyId] - Required: Property ID
 * @param {string} [params.unit_number] - Optional: Unit number filter
 * @param {string} [params.type] - Optional: "apartment" or "townhouse"
 * @param {string} [params.startDate] - Optional: YYYY-MM-DD
 * @param {string} [params.endDate] - Optional: YYYY-MM-DD
 * @param {string} [params.overall_status] - Optional: "passed" or "has issues"
 * @returns {Promise<void>} Triggers file download
 */
export const exportSuiteInspectionsToExcel = async (params = {}) => {
  if (!params.propertyId) {
    toast.error("Property ID is required to export inspections");
    return;
  }

  try {
    toast.info("Generating Excel file...");

    const response = await axios.get(ENDPOINTS.EXPORT_EXCEL, {
      params,
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let fileName = `Suite_Inspection_Checklists_${new Date().toISOString().split('T')[0]}.xlsx`;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
      if (fileNameMatch?.[1]) {
        fileName = fileNameMatch[1];
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("Excel file downloaded successfully");
  } catch (error) {
    handleApiError(error, "Failed to export suite inspections to Excel");
  }
};